import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Button,
  DateField,
  Field,
  Modal,
  ModalActions,
  Select,
  TextArea,
} from '@evogard/ui';
import {
  businessDate,
  deskColumnKey,
  equalShares,
  parseMoneyInput,
  toColumn,
  type DeskColumn,
} from '@evogard/domain';
import type { DeskPlateLookup } from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { columnTitle, type TenantSummary } from './deskBoard.js';
import { useInvalidateDesk } from './useDesk.js';

/**
 * Distribuir uma fila, ou redistribuir a coluna de um operador — os modais
 * "Distribuir Clientes" e "Redistribuir" do legado (`App.tsx:5930`, `:4403`).
 *
 * É por base: a distribuição é um ato do servidor, numa transação, e cada base
 * tem a sua lista de operadores. A divisão começa igual (`equalShares`) e a
 * pessoa ajusta operador a operador; nunca passa do que a coluna tem.
 *
 * A coluna pode juntar várias chaves — a fila mãe de vencimento junta as seis
 * faixas do dia do mês. O servidor distribui uma chave por vez, então, quando
 * mais de uma tem card na base escolhida, a pessoa escolhe a faixa.
 */
export function DistributeDialog({
  columns,
  title,
  summaries,
  me,
  onClose,
}: {
  columns: DeskColumn[];
  title: string;
  summaries: TenantSummary[];
  me: string;
  onClose: () => void;
}) {
  const invalidate = useInvalidateDesk();
  const keys = columns.map(deskColumnKey);
  const column = columns[0]!;
  const countIn = (summary: TenantSummary['summary'], key: string) =>
    summary.columns.find((c) => c.key === key)?.count ?? 0;
  const allowed = summaries.filter(({ summary }) => {
    if (!keys.some((k) => countIn(summary, k) > 0)) return false;
    if (summary.can.distribute) return true;
    return column.kind === 'operador' && column.operatorId === me && summary.can.assign;
  });
  const [slug, setSlug] = useState(allowed[0]?.tenant.slug ?? '');
  const chosen = allowed.find((s) => s.tenant.slug === slug);
  const bands = chosen ? keys.filter((k) => countIn(chosen.summary, k) > 0) : [];
  const [picked, setPicked] = useState('');
  const key = bands.includes(picked) ? picked : (bands[0] ?? keys[0]!);
  const available = chosen ? countIn(chosen.summary, key) : 0;
  const targets = (chosen?.summary.operators ?? []).filter(
    (o) => o.listed && o.activeFlow && !(column.kind === 'operador' && o.userId === column.operatorId),
  );
  const [counts, setCounts] = useState<Record<string, number>>({});
  const total = targets.reduce((n, o) => n + (counts[o.userId] ?? 0), 0);

  const run = useMutation({
    mutationFn: async () =>
      ok(
        await api.billingDesk.distribute({
          params: { tenant: slug },
          body: {
            column: key,
            shares: targets.map((o) => ({ operatorId: o.userId, count: counts[o.userId] ?? 0 })),
          },
        }),
        200,
        'Não foi possível distribuir.',
      ),
    onSuccess: () => invalidate(),
  });

  const equally = () => {
    const shares = equalShares(available, targets.length);
    setCounts(Object.fromEntries(targets.map((o, i) => [o.userId, shares[i] ?? 0])));
  };

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Distribuir — ${title}`} size="lg">
      {run.data ? (
        <div className="flex flex-col gap-3">
          <Alert tone="sucesso">{run.data.assigned} card(s) distribuídos.</Alert>
          <ModalActions>
            <Button onClick={onClose}>Fechar</Button>
          </ModalActions>
        </div>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            run.mutate();
          }}
        >
          {allowed.length === 0 && (
            <Alert tone="informacao">Nenhuma base sua tem card nesta coluna para distribuir.</Alert>
          )}
          {allowed.length > 1 && (
            <Select
              label="Base"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setCounts({});
              }}
            >
              {allowed.map((s) => (
                <option key={s.tenant.slug} value={s.tenant.slug}>
                  {s.tenant.name}
                </option>
              ))}
            </Select>
          )}
          {bands.length > 1 && chosen && (
            <Select
              label="Faixa"
              value={key}
              onChange={(e) => {
                setPicked(e.target.value);
                setCounts({});
              }}
            >
              {bands.map((k) => (
                <option key={k} value={k}>
                  {columnTitle(k, undefined)} ({countIn(chosen.summary, k)})
                </option>
              ))}
            </Select>
          )}
          <p className="text-sm text-texto-suave">
            Disponíveis: <strong className="text-texto">{available}</strong> · A distribuir:{' '}
            <strong className="text-texto">{total}</strong> · Ficam:{' '}
            <strong className="text-texto">{Math.max(available - total, 0)}</strong>
          </p>
          <ul className="flex flex-col gap-2">
            {targets.map((o) => (
              <li key={o.userId} className="flex items-end gap-3">
                <span className="flex-1 text-sm">{o.name}</span>
                <div className="w-28">
                  <Field
                    label={`Cards para ${o.name}`}
                    labelHidden
                    type="number"
                    min={0}
                    value={counts[o.userId] ?? 0}
                    onChange={(e) =>
                      setCounts((c) => ({ ...c, [o.userId]: Math.max(0, Number(e.target.value) || 0) }))
                    }
                  />
                </div>
              </li>
            ))}
          </ul>
          {targets.length === 0 && chosen && (
            <Alert tone="informacao">
              Nenhum operador ativo na lista desta base para receber. A lista é da configuração da Mesa.
            </Alert>
          )}
          {total > available && (
            <Alert tone="alerta">A divisão pede mais cards do que a coluna tem.</Alert>
          )}
          {run.error && <Alert tone="erro">{run.error.message}</Alert>}
          <ModalActions>
            <Button type="button" variant="contorno" onClick={equally} disabled={targets.length === 0}>
              Distribuir igualmente
            </Button>
            <Button type="submit" loading={run.isPending} disabled={total === 0 || total > available}>
              Confirmar distribuição
            </Button>
          </ModalActions>
        </form>
      )}
    </Modal>
  );
}

interface PlateRow {
  plate: string;
  dueDate: string;
  amount: string;
  /** O que a placa achou no cadastro, para o texto digitado em `plate`. */
  lookup: { plate: string; result: DeskPlateLookup } | null;
}

const emptyRow = (): PlateRow => ({ plate: '', dueDate: businessDate(new Date()), amount: '', lookup: null });

/** A frase embaixo da placa — o que o servidor vai gravar para ela. */
function lookupText(result: DeskPlateLookup): string {
  if (result.kind === 'cadastro' && result.vehicle) {
    return `No cadastro: ${result.vehicle.memberName ?? 'associado sem nome'}${result.vehicle.code ? ` · ${result.vehicle.code}` : ''} — nome, CPF e consultor vêm de lá.`;
  }
  if (result.kind === 'ambigua') {
    return `Esta placa aparece em ${result.count} veículos do cadastro — o card guarda o nome que você digitar.`;
  }
  return 'Placa fora do cadastro desta base — o card guarda o nome que você digitar.';
}

/**
 * Cadastrar um card à mão — "Cadastrar Novo Cliente" do legado (`App.tsx:6271`).
 * Uma linha por placa, cada uma vira um card; placa repetida no mesmo mês é
 * recusada pelo servidor, com a mensagem de quem já tem o card.
 *
 * Ao sair do campo de placa, a tela pergunta ao servidor o que ela acha no
 * cadastro (`lookupPlate`, a mesma regra do `create`): a placa de UM veículo
 * faz o card apontar para ele, e nome, CPF e consultor deixam de ser pedidos —
 * vêm do cadastro. Os campos só aparecem quando alguma placa fica de fora.
 */
export function CreateCardDialog({
  summaries,
  onClose,
}: {
  summaries: TenantSummary[];
  onClose: () => void;
}) {
  const invalidate = useInvalidateDesk();
  const allowed = summaries.filter((s) => s.summary.can.create || s.summary.can.receptive);
  const [slug, setSlug] = useState(allowed[0]?.tenant.slug ?? '');
  const can = allowed.find((s) => s.tenant.slug === slug)?.summary.can;
  const [flow, setFlow] = useState<'ativa' | 'receptiva'>(can?.create ? 'ativa' : 'receptiva');
  const [name, setName] = useState('');
  const [document, setDocument] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [protocol, setProtocol] = useState('');
  const [consultant, setConsultant] = useState('');
  const [plates, setPlates] = useState<PlateRow[]>([emptyRow()]);

  /*
   * A resposta vale para o texto que foi perguntado: digitar de novo na placa
   * apaga a resposta (`setRow`), e trocar de base pergunta tudo de novo.
   */
  const lookup = async (i: number, plate: string) => {
    if (plate.trim() === '' || !slug) return;
    const r = await api.billingDesk.lookupPlate({ params: { tenant: slug }, body: { flow, plate } });
    if (r.status !== 200) return;
    setPlates((rows) =>
      rows.map((row, j) => (j === i && row.plate === plate ? { ...row, lookup: { plate, result: r.body } } : row)),
    );
  };
  /* nome, CPF e consultor só fazem falta se alguma placa não achou UM veículo */
  const needsTyped = plates.some((p) => p.lookup?.result.kind !== 'cadastro');

  const save = useMutation({
    mutationFn: async () => {
      const rows = plates.map((p) => {
        const amount = parseMoneyInput(p.amount);
        if (amount === null) throw new Error(`Valor inválido para a placa ${p.plate || '(vazia)'}.`);
        return { plate: p.plate, dueDate: p.dueDate, amount: toColumn(amount) };
      });
      return ok(
        await api.billingDesk.create({
          params: { tenant: slug },
          body: {
            flow,
            name: needsTyped ? name.trim() || null : null,
            document: needsTyped ? document.trim() || null : null,
            phone,
            note: note.trim() || null,
            conversationRef: protocol.trim() || null,
            consultant: needsTyped ? consultant.trim() || null : null,
            plates: rows,
          },
        }),
        201,
        'Não foi possível cadastrar.',
      );
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const setRow = (i: number, patch: Partial<PlateRow>) =>
    setPlates((rows) =>
      rows.map((r, j) =>
        j === i ? { ...r, ...patch, ...(patch.plate !== undefined ? { lookup: null } : {}) } : r,
      ),
    );

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Novo card" size="lg">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {allowed.length > 1 && (
          <Select
            label="Base"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setPlates((rows) => rows.map((r) => ({ ...r, lookup: null })));
            }}
          >
            {allowed.map((s) => (
              <option key={s.tenant.slug} value={s.tenant.slug}>
                {s.tenant.name}
              </option>
            ))}
          </Select>
        )}
        {can?.create && can.receptive && (
          <Select label="Tipo" value={flow} onChange={(e) => setFlow(e.target.value as 'ativa' | 'receptiva')}>
            <option value="ativa">Cobrança ativa</option>
            <option value="receptiva">Receptiva — quem ligou</option>
          </Select>
        )}
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-texto">Placas, vencimentos e valores</legend>
          {plates.map((p, i) => (
            <div key={i} className="flex flex-col gap-1">
              <div className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
                <Field
                  label="Placa"
                  required
                  value={p.plate}
                  onChange={(e) => setRow(i, { plate: e.target.value })}
                  onBlur={(e) => void lookup(i, e.target.value)}
                />
                <DateField label="Vencimento" required value={p.dueDate} onChange={(v) => setRow(i, { dueDate: v })} />
                <Field
                  label="Valor do boleto"
                  required
                  inputMode="decimal"
                  value={p.amount}
                  onChange={(e) => setRow(i, { amount: e.target.value })}
                />
                <Button
                  type="button"
                  variant="discreto"
                  size="sm"
                  disabled={plates.length === 1}
                  onClick={() => setPlates((rows) => rows.filter((_, j) => j !== i))}
                >
                  Tirar
                </Button>
              </div>
              {p.lookup && (
                <p className="text-sm text-texto-suave" aria-live="polite">
                  {lookupText(p.lookup.result)}
                </p>
              )}
            </div>
          ))}
          <div>
            <Button
              type="button"
              variant="contorno"
              size="sm"
              icon="mais"
              onClick={() => setPlates((rows) => [...rows, emptyRow()])}
            >
              Outra placa
            </Button>
          </div>
        </fieldset>
        {needsTyped && (
          <>
            <Field label="Nome completo" required value={name} onChange={(e) => setName(e.target.value)} />
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="CPF" value={document} onChange={(e) => setDocument(e.target.value)} />
              <Field label="Consultor" value={consultant} onChange={(e) => setConsultant(e.target.value)} />
            </div>
          </>
        )}
        <Field label="Telefone" required value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Field label="Protocolo" value={protocol} onChange={(e) => setProtocol(e.target.value)} />
        <TextArea label="Observação" value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} />
        {save.error && <Alert tone="erro">{save.error.message}</Alert>}
        <ModalActions>
          <Button type="button" variant="contorno" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={save.isPending} disabled={!slug}>
            Cadastrar
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
}
